import { endOfDay, format, formatDistanceToNow, isPast, parseISO } from "date-fns";
import type { LoanDto } from "src/backend";
import type { LoanDtoExtended } from "src/types/loan";

import { extendAppUserDTO } from "../user/extend-app-user-dto";
import { getLoanApplicant } from "./get-loan-applicant";

const safeFormatDistanceToNow = (date: Date | null): string => {
    try {
        return date ? formatDistanceToNow(date) : '';
    } catch {
        return '';
    }
};

const safeFormat = (date: Date | null, dateFormat: string): string => {
    return date ? format(date, dateFormat) : '';
};

export const transformLoan = (loan: LoanDto): LoanDtoExtended => {
    if (!loan) {
        return null;
    }

    const hasCloseDate = !!loan.closeDate;
    const parsedCloseDate = hasCloseDate ? parseISO(loan.closeDate) : null;

    const closeDateDistance = safeFormatDistanceToNow(parsedCloseDate);
    const lastActivityDistance = safeFormatDistanceToNow(loan.phaseEnteredDate ? parseISO(loan.phaseEnteredDate) : null);

    const applicantEntity = getLoanApplicant(loan.loanEntities);

    return {
        ...loan,
        closeDate: loan.closeDate,
        shortCode: loan.shortCode?.toUpperCase(),
        loanProgress: (loan.percentageApproved + loan.percentageAnswered) / 2,
        closeDateDistance,
        elapsedDaysInLoanPhaseFormatted: loan.phaseEnteredDate ? `${lastActivityDistance} in ${loan.loanPhase?.name}` : '',
        color: loan.color ?? '#FAC014',
        closeDateYearFormatted: safeFormat(parsedCloseDate, "MMM d yyyy").toUpperCase(),
        createdDateYearFormatted: safeFormat(loan.createdDate ? parseISO(loan.createdDate) : null, "MMM d yyyy").toUpperCase(),
        closeDateFormatted: safeFormat(parsedCloseDate, "MMM d, yyyy"),
        createdDateFormatted: safeFormat(loan.createdDate ? parseISO(loan.createdDate) : null, "MMM d, yyyy"),
        closeDateIsPassed: hasCloseDate && parsedCloseDate ? isPast(endOfDay(parsedCloseDate)) : false,
        templateProjectName: loan.template?.name || loan.templateProjectName,
        lastModifiedFormatted: safeFormat(loan.lastModifiedDate ? parseISO(loan.lastModifiedDate) : null, "MMM d, yyyy"),
        lastModifiedByUser: extendAppUserDTO(loan.lastModifiedByUser),
        applicantEntity,
        loanRoles: loan.loanRoles.map(loanRole => ({
            ...loanRole,
            user: extendAppUserDTO(loanRole.user),
        })),
    };
};
